<template>
  <footer id="app-footer">
    <div class="container">
      <div class="footer-grid">

        <!-- Identity -->
        <div class="footer-col footer-identity">
          <div class="footer-brand">
            <span class="footer-mark">✦</span>
            <span class="footer-acronym">
              {{ currentLanguage === 'en' ? content.organization.acronym : currentLanguage === 'zh-Hant' ? content.organization.nameZhHant : content.organization.nameZhHans }}
            </span>
          </div>
          <p class="footer-full-name">
            {{ currentLanguage === 'en' ? content.organization.name : currentLanguage === 'zh-Hant' ? content.organization.fullNameZhHant : content.organization.fullNameZhHans }}
          </p>
          <p class="footer-tagline">
            {{ currentLanguage === 'en'
              ? 'Supporting Chinese Lutheran pastors, congregations, and mission partners in North America and around the world.'
              : currentLanguage === 'zh-Hant'
                ? '支持北美及全球各地的華人路德宗牧師、教會和宣教夥伴。'
                : '支持北美及全球各地的华人路德宗牧师、教会和宣教伙伴。'
            }}
          </p>
          <p class="footer-rso">
            {{ currentLanguage === 'en'
              ? 'An LCMS Recognized Service Organization'
              : currentLanguage === 'zh-Hant'
                ? 'LCMS認可服務組織'
                : 'LCMS认可服务组织' }}
          </p>
        </div>

        <!-- Explore -->
        <div class="footer-col">
          <h4 class="footer-heading">
            {{ currentLanguage === 'en' ? 'Explore' : currentLanguage === 'zh-Hant' ? '瀏覽' : '浏览' }}
          </h4>
          <ul class="footer-links">
            <li v-for="item in content.navigation?.items" :key="item.path">
              <router-link :to="item.path">{{ getNavLabel(item) }}</router-link>
            </li>
          </ul>
        </div>

        <!-- Connect -->
        <div class="footer-col">
          <h4 class="footer-heading">
            {{ currentLanguage === 'en' ? 'Connect' : currentLanguage === 'zh-Hant' ? '聯繫' : '联系' }}
          </h4>
          <ul class="footer-links">
            <li v-if="content.organization?.website">
              <a :href="'https://' + content.organization.website.replace('https://','').replace('http://','')"
                 target="_blank" rel="noopener noreferrer">
                {{ content.organization.website.replace('https://','').replace('http://','') }}
              </a>
            </li>
            <li v-if="content.organization?.social?.facebook">
              <a :href="content.organization.social.facebook" target="_blank" rel="noopener noreferrer">Facebook</a>
            </li>
            <li v-if="content.organization?.social?.youtube">
              <a :href="content.organization.social.youtube" target="_blank" rel="noopener noreferrer">YouTube</a>
            </li>
          </ul>
          <p class="footer-parent-org">
            {{ currentLanguage === 'en' ? content.organization.parentOrg : currentLanguage === 'zh-Hant' ? content.organization.parentOrgZhHant : content.organization.parentOrgZhHans }}
          </p>
        </div>

      </div>

      <div class="footer-bottom">
        <p>© {{ currentYear }} {{ content.organization.fullNameZhHant || content.organization.fullNameZh }}</p>
        <p>{{ currentLanguage === 'en' ? 'All rights reserved.' : currentLanguage === 'zh-Hant' ? '版權所有。' : '版权所有。' }}</p>
      </div>
    </div>
  </footer>
</template>

<script>
import { languageStore } from '../store/language'

export default {
  name: 'FooterComponent',
  props: {
    content: { type: Object, required: true }
  },
  computed: {
    currentLanguage() { return languageStore.currentLanguage },
    currentYear()     { return new Date().getFullYear() }
  },
  methods: {
    getNavLabel(item) {
      if (this.currentLanguage === 'en')      return item.label
      if (this.currentLanguage === 'zh-Hant') return item.labelZhHant || item.labelZh
      return item.labelZhHans || item.labelZh
    }
  }
}
</script>

<style scoped>
.footer-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 3rem;
  padding-bottom: 2.5rem;
  border-bottom: 1px solid rgba(255,255,255,.1);
  margin-bottom: 2rem;
}

/* Identity column */
.footer-brand {
  display: flex;
  align-items: center;
  gap: .5rem;
  margin-bottom: .75rem;
}

.footer-mark {
  color: var(--color-primary-light);
  font-size: 1.1rem;
}

.footer-acronym {
  font-family: var(--font-family-heading);
  font-size: 1.4rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: -.01em;
}

.footer-full-name {
  font-size: .85rem;
  font-weight: 600;
  color: rgba(255,255,255,.6);
  text-transform: uppercase;
  letter-spacing: .05em;
  margin-bottom: .9rem;
  line-height: 1.4;
}

.footer-tagline {
  font-size: .9rem;
  color: rgba(255,255,255,.65);
  line-height: 1.7;
  margin-bottom: .75rem;
}

.footer-rso {
  font-size: .78rem;
  color: rgba(255,255,255,.4);
  font-style: italic;
  margin: 0;
}

/* Nav columns */
.footer-heading {
  font-family: var(--font-family-heading);
  font-size: .78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .1em;
  color: rgba(255,255,255,.45);
  margin-bottom: 1rem;
}

.footer-links {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: .45rem;
}

.footer-links a {
  font-size: .9rem;
  color: rgba(255,255,255,.7);
  transition: color .15s;
}

.footer-links a:hover { color: #fff; }

.footer-parent-org {
  margin-top: 1.25rem;
  font-size: .78rem;
  color: rgba(255,255,255,.35);
  font-style: italic;
  line-height: 1.5;
}

/* Bottom bar */
.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: .5rem;
  font-size: .82rem;
  color: rgba(255,255,255,.35);
}

/* Responsive */
@media (max-width: 768px) {
  .footer-grid {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }
  .footer-identity { grid-column: 1 / -1; }
  .footer-bottom { flex-direction: column; text-align: center; }
}

@media (max-width: 480px) {
  .footer-grid { grid-template-columns: 1fr; }
}
</style>
